import { LimitUnit as ProtoLimitUnit } from '@/lib/protobuf/gen/errors/errors_pb';

/**
 * A limit the server reports in GET /api/v1/features `limits` (LIMIT-05). The
 * unit strings are domain.LimitUnit.String() on the Go side — the same strings
 * LIMIT-03's refusal body uses. An absent key means "not reported", never 0.
 */
export type LimitUnit = 'items' | 'characters' | 'bytes' | 'seconds' | 'requests';

export interface ReportedLimit {
  max?: number;
  /** Lower bound of a range (design §1). No slice-a reader on the web; parsed so a range limit (LIMIT-34) needs no parser change. */
  min?: number;
  unit: LimitUnit;
  unlimited?: boolean;
}

const UNITS: readonly LimitUnit[] = ['items', 'characters', 'bytes', 'seconds', 'requests'];

/** isLimitUnit is the one guard for a server-named unit: the features payload and LIMIT-03's refusals both use it. */
export function isLimitUnit(v: unknown): v is LimitUnit {
  return typeof v === 'string' && (UNITS as readonly string[]).includes(v);
}

/**
 * The web unit for a proto `LimitUnit` (errors.proto), or undefined for UNSPECIFIED or a value this bundle does not
 * know. The one mapping both readers use: LIMIT-03's refusals (`fromEnvelope.ts`) and LIMIT-32's import cuts.
 */
export function limitUnitFromProto(u: number): LimitUnit | undefined {
  const name = (ProtoLimitUnit as unknown as Record<number, unknown>)[u];
  const unit = typeof name === 'string' ? name.toLowerCase() : undefined;
  return isLimitUnit(unit) ? unit : undefined;
}

/**
 * The `limits` field as the store keeps it. Anything malformed is dropped, not
 * defaulted: an entry with no positive max and no `unlimited` is "not known",
 * and the UI then shows no counter and sets no client cap.
 */
export function parseLimits(raw: unknown): Record<string, ReportedLimit> {
  const out: Record<string, ReportedLimit> = {};
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'object' || value === null) continue;
    const v = value as Record<string, unknown>;
    if (!isLimitUnit(v.unit)) continue;
    const unlimited = v.unlimited === true;
    const max =
      typeof v.max === 'number' && Number.isFinite(v.max) && v.max > 0 ? v.max : undefined;
    if (!unlimited && max === undefined) continue;
    const min =
      typeof v.min === 'number' && Number.isFinite(v.min) && v.min > 0 ? v.min : undefined;
    const limit: ReportedLimit = { unit: v.unit as LimitUnit };
    if (max !== undefined) limit.max = max;
    if (min !== undefined) limit.min = min;
    if (unlimited) limit.unlimited = true;
    out[key] = limit;
  }
  return out;
}
